import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  UploadSimple,
  FolderSimple,
  FolderPlus,
  MagnifyingGlass,
  Trash,
  DownloadSimple,
  Copy,
  X,
  FilePdf,
  FileDoc,
  FileXls,
  FileText,
  Image as ImageIcon,
  VideoCamera,
  SpeakerHigh,
  PencilRuler,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { api, API, formatApiErrorDetail, getAssetToken } from "@/lib/api";

const CHUNK = 4 * 1024 * 1024;
const ALL = "__all__";

const ICONS = {
  image: ImageIcon,
  video: VideoCamera,
  audio: SpeakerHigh,
  pdf: FilePdf,
  drawing: PencilRuler,
  sheet: FileXls,
  doc: FileDoc,
  file: FileText,
};

const KINDS = [
  ["", "Everything"],
  ["image", "Photos"],
  ["pdf", "PDFs"],
  ["sheet", "Spreadsheets"],
  ["doc", "Documents"],
  ["drawing", "Drawings"],
  ["video", "Video"],
];

const size = (n) => {
  if (!n) return "0 KB";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fileUrl = (f, download) =>
  `${API}/media/file/${f.storage_path}?t=${getAssetToken()}${download ? "&download=1" : ""}`;

const thumbUrl = (f) =>
  f.thumb_path ? `${API}/media/file/${f.thumb_path}?t=${getAssetToken()}` : fileUrl(f);

export default function MediaLibrary() {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState({
    folders: [],
    used: [],
    bytes: 0,
    count: 0,
  });
  const [folder, setFolder] = useState(ALL);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [list, meta] = await Promise.all([
        api.get("/media", {
          params: { folder: folder === ALL ? "" : folder, kind, q },
        }),
        api.get("/media/stats"),
      ]);
      setFiles(list.data.files || []);
      setStats(meta.data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not open the library",
      );
    }
    setLoading(false);
  }, [folder, kind, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const send = async (file) => {
    const id = crypto.randomUUID();
    const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
    setQueue((c) => [...c, { id, name: file.name, pct: 0 }]);
    const bump = (pct) =>
      setQueue((c) => c.map((r) => (r.id === id ? { ...r, pct } : r)));
    try {
      for (let i = 0; i < chunks; i += 1) {
        const fd = new FormData();
        fd.append("upload_id", id);
        fd.append("index", String(i));
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part");
        await api.post("/media/chunk", fd);
        bump(Math.round(((i + 1) / chunks) * 92));
      }
      await api.post("/media/finish", {
        upload_id: id,
        filename: file.name,
        folder: folder === ALL ? "Unfiled" : folder,
        content_type: file.type || "application/octet-stream",
        chunks,
      });
      bump(100);
      setTimeout(() => setQueue((c) => c.filter((r) => r.id !== id)), 1200);
      return true;
    } catch (err) {
      setQueue((c) => c.filter((r) => r.id !== id));
      toast.error(
        `${file.name}: ${
          formatApiErrorDetail(err.response?.data?.detail) || "upload failed"
        }`,
      );
      return false;
    }
  };

  const take = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    let ok = 0;
    for (const file of picked) {
      // eslint-disable-next-line no-await-in-loop
      if (await send(file)) ok += 1;
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    load();
  };

  const addFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    try {
      await api.post("/media/folders", { name });
      setNewFolder("");
      setFolder(name);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add that folder",
      );
    }
  };

  const dropFolder = async (name) => {
    if (!window.confirm(`Delete the folder "${name}"? Files move to Unfiled.`))
      return;
    await api.delete(`/media/folders/${encodeURIComponent(name)}`);
    setFolder(ALL);
    load();
    toast.success("Folder removed");
  };

  const move = async (file, to) => {
    await api.patch(`/media/${file.id}`, { folder: to });
    load();
  };

  const remove = async (file) => {
    if (!window.confirm(`Delete ${file.name}?`)) return;
    await api.delete(`/media/${file.id}`);
    setPreview(null);
    load();
    toast.success("Deleted");
  };

  const copyLink = (file) => {
    navigator.clipboard?.writeText(fileUrl(file));
    toast.success("Link copied");
  };

  const counts = Object.fromEntries(
    (stats.used || []).map((u) => [u.folder, u.count]),
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Files & Media" />
      <main className="relative z-10 pb-28 pt-6" data-testid="media-page">
        <Link
          to="/settings"
          data-testid="media-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Files &amp; Media
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Supplier price lists, care guides, warranties, install photos and
          video — kept in your storage, not on the machine.
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-[15rem_1fr] lg:items-start">
          <aside className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <div
              className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-zinc-500"
              data-testid="media-storage"
            >
              {stats.count || 0} files · {size(stats.bytes)}
            </div>
            <div className="mt-3 space-y-1" data-testid="media-folders">
              <button
                type="button"
                data-testid="media-folder-all"
                onClick={() => setFolder(ALL)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors duration-150 ${
                  folder === ALL
                    ? "bg-zinc-800 text-zinc-100"
                    : "text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                }`}
              >
                <FolderSimple size={14} weight="bold" /> All files
                <span className="font-mono-data ml-auto text-[10px] text-zinc-600">
                  {stats.count || 0}
                </span>
              </button>
              {["Unfiled", ...(stats.folders || [])].map((name) => (
                <div key={name} className="group flex items-center gap-1">
                  <button
                    type="button"
                    data-testid={`media-folder-${name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setFolder(name)}
                    className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors duration-150 ${
                      folder === name
                        ? "bg-zinc-800 text-zinc-100"
                        : "text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                    }`}
                  >
                    <FolderSimple
                      size={14}
                      weight="bold"
                      className="shrink-0"
                    />
                    <span className="truncate">{name}</span>
                    <span className="font-mono-data ml-auto text-[10px] text-zinc-600">
                      {counts[name] || 0}
                    </span>
                  </button>
                  {name !== "Unfiled" && (
                    <button
                      type="button"
                      aria-label={`Delete folder ${name}`}
                      onClick={() => dropFolder(name)}
                      className="shrink-0 p-1.5 text-zinc-700 opacity-0 transition-opacity duration-150 hover:text-red-400 group-hover:opacity-100"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-1 border-t border-zinc-800 pt-3">
              <input
                data-testid="media-new-folder"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addFolder()}
                placeholder="New folder"
                className="min-w-0 flex-1 rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-2 text-xs text-zinc-200 outline-none focus:border-indigo-500"
              />
              <button
                type="button"
                data-testid="media-add-folder"
                onClick={addFolder}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500 text-white hover:bg-indigo-400"
                aria-label="Add folder"
              >
                <FolderPlus size={15} weight="bold" />
              </button>
            </div>
          </aside>

          <section className="min-w-0">
            <div
              data-testid="media-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                take(e.dataTransfer.files);
              }}
              onClick={() => inputRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors duration-150 ${
                dragging
                  ? "border-indigo-400 bg-indigo-500/10"
                  : "border-zinc-800 bg-zinc-900/50 hover:border-indigo-400/50"
              }`}
            >
              <UploadSimple
                size={22}
                weight="bold"
                className="mx-auto text-indigo-400"
              />
              <div className="mt-2 text-sm font-bold text-zinc-200">
                Drop files here, or click to choose
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                Photos, PDFs, Word, Excel, DXF, video — up to 120MB each, into{" "}
                <span className="text-zinc-300">
                  {folder === ALL ? "Unfiled" : folder}
                </span>
              </div>
              <input
                ref={inputRef}
                data-testid="media-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  take(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {queue.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="media-queue">
                {queue.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-lg border border-zinc-800 bg-zinc-900 p-3"
                  >
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="min-w-0 flex-1 truncate">{r.name}</span>
                      <span className="font-mono-data text-[10px] text-zinc-500">
                        {r.pct}%
                      </span>
                    </div>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-zinc-800">
                      <div
                        className="h-full rounded-full bg-indigo-500 transition-all duration-200"
                        style={{ width: `${r.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[13rem] flex-1">
                <MagnifyingGlass
                  size={14}
                  weight="bold"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
                />
                <input
                  data-testid="media-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search file names and notes"
                  className="h-10 w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-9 pr-3 text-sm text-zinc-200 outline-none focus:border-indigo-500"
                />
              </div>
              <div className="flex flex-wrap gap-1">
                {KINDS.map(([value, label]) => (
                  <button
                    key={value || "all"}
                    type="button"
                    data-testid={`media-kind-${value || "all"}`}
                    onClick={() => setKind(value)}
                    className={`h-9 rounded-lg px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      kind === value
                        ? "bg-zinc-100 text-zinc-900"
                        : "border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
              data-testid="media-grid"
            >
              {files.map((f) => {
                const Icon = ICONS[f.kind] || FileText;
                return (
                  <div
                    key={f.id}
                    data-testid={`media-item-${f.id}`}
                    className="group overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-indigo-400/40"
                  >
                    <button
                      type="button"
                      onClick={() => setPreview(f)}
                      data-testid={`media-open-${f.id}`}
                      className="flex h-32 w-full items-center justify-center overflow-hidden bg-zinc-950"
                    >
                      {f.kind === "image" ? (
                        <img
                          src={thumbUrl(f)}
                          alt={f.name}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <Icon
                          size={30}
                          weight="bold"
                          className="text-zinc-600"
                        />
                      )}
                    </button>
                    <div className="p-3">
                      <div className="truncate text-xs font-bold text-zinc-200">
                        {f.name}
                      </div>
                      <div className="font-mono-data mt-1 text-[10px] uppercase tracking-widest text-zinc-600">
                        {f.kind} · {size(f.size)}
                      </div>
                      <div className="mt-2 flex items-center gap-1">
                        <select
                          data-testid={`media-move-${f.id}`}
                          value={f.folder}
                          onChange={(e) => move(f, e.target.value)}
                          className="h-8 min-w-0 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-[11px] text-zinc-300"
                        >
                          {["Unfiled", ...(stats.folders || [])].map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => copyLink(f)}
                          aria-label="Copy link"
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-white"
                        >
                          <Copy size={13} weight="bold" />
                        </button>
                        <a
                          href={fileUrl(f, true)}
                          data-testid={`media-download-${f.id}`}
                          aria-label="Download"
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-white"
                        >
                          <DownloadSimple size={13} weight="bold" />
                        </a>
                        <button
                          type="button"
                          data-testid={`media-delete-${f.id}`}
                          onClick={() => remove(f)}
                          aria-label="Delete"
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400"
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {!loading && files.length === 0 && (
              <div
                className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
                data-testid="media-empty"
              >
                <div className="text-sm font-bold text-zinc-300">
                  Nothing here yet
                </div>
                <p className="mt-1 text-xs text-zinc-500">
                  Drop your first price list or install photos above.
                </p>
              </div>
            )}
          </section>
        </div>
      </main>

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          data-testid="media-preview"
          onClick={() => setPreview(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-zinc-800 p-3">
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-zinc-200">
                {preview.name}
              </span>
              <a
                href={fileUrl(preview, true)}
                className="flex h-9 items-center gap-2 rounded-lg border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:text-white"
              >
                <DownloadSimple size={13} weight="bold" /> Download
              </a>
              <button
                type="button"
                data-testid="media-preview-close"
                onClick={() => setPreview(null)}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-900 hover:text-white"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="max-h-[75vh] overflow-auto bg-zinc-900 p-4">
              {preview.kind === "image" && (
                <img
                  src={fileUrl(preview)}
                  alt={preview.name}
                  className="mx-auto max-h-[68vh] object-contain"
                />
              )}
              {preview.kind === "video" && (
                <video
                  src={fileUrl(preview)}
                  controls
                  className="mx-auto max-h-[68vh] w-full"
                />
              )}
              {preview.kind === "audio" && (
                <audio src={fileUrl(preview)} controls className="w-full" />
              )}
              {preview.kind === "pdf" && (
                <iframe
                  src={fileUrl(preview)}
                  title={preview.name}
                  className="h-[68vh] w-full rounded border border-zinc-800 bg-white"
                />
              )}
              {!["image", "video", "audio", "pdf"].includes(preview.kind) && (
                <div className="py-12 text-center text-sm text-zinc-400">
                  No preview for this file type — download it to open.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
